import React from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  HardHat,
  CloudRain,
  Ruler,
  Palette,
  Truck,
  Flame,
  Zap,
  Layers,
  ShieldCheck,
  Scissors,
} from "lucide-react";

const HERO_MONTAGE =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_landing-forge-60/artifacts/t0cc0oum_WhatsApp%20Image%202026-09-16%20at%2016.16.44.jpeg";
const HERO_ACTION =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_landing-forge-60/artifacts/v2ctyiru_WhatsApp%20Image%202026-09-16%20at%2016.14.01%20%281%29.jpeg";
const HERO_FLATLAY =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_landing-forge-60/artifacts/ec0254q5_WhatsApp%20Image%202026-09-16%20at%2016.14.01.jpeg";

const coverallTypes = [
  {
    icon: Layers,
    name: "Standard Cotton Dangri",
    tag: "Everyday",
    blurb:
      "220–240 GSM 100% cotton drill. The classic mechanic-shop coverall. Breathable, easy to wash, takes embroidery beautifully.",
  },
  {
    icon: HardHat,
    name: "Poly-Cotton Coverall",
    tag: "Durable",
    blurb:
      "65/35 poly-cotton blend. Stronger, wrinkle-resistant, faster to dry — ideal for warehouses, garages and delivery fleets.",
  },
  {
    icon: Flame,
    name: "FR (Flame-Retardant) Coverall",
    tag: "Safety",
    blurb:
      "IS/EN-compliant FR-treated cotton. Certified protection for welders, electricians, oil & gas crews and hot-work zones.",
  },
  {
    icon: Zap,
    name: "Hi-Viz Reflective Coverall",
    tag: "Visibility",
    blurb:
      "2\" silver reflective tape on chest, sleeves and legs. Meets EN ISO 20471 visibility standards for site and road-work teams.",
  },
  {
    icon: ShieldCheck,
    name: "Two-Piece Coverall (Jacket + Pant)",
    tag: "Modular",
    blurb:
      "Split jacket + trouser for teams that want mix-and-match sizing or lighter summer wear. Same fabrics, same customisation.",
  },
  {
    icon: Scissors,
    name: "Denim Coverall",
    tag: "Heritage",
    blurb:
      "Washed denim boiler suit for automotive garages, breweries and heritage brand uniforms. Rugged, ages beautifully, big cargo pockets.",
  },
];

const products = [
  {
    id: "coveralls",
    title: "Industrial Coveralls · Dangris",
    icon: HardHat,
    bg: "#FFE600",
    fg: "#0A0A0A",
    imageBanner: true,
    blurb:
      "Heavy-duty single-piece coveralls (also known as dangris or boiler suits) stitched for factories, garages, oil rigs, electrical crews, food-processing units and shipyards. Reinforced stress points, multiple utility pockets, brass YKK-style front zip. Fully customisable in fabric, colour, embroidery, screen-printed logos and reflective tape placement.",
    specs: [
      "Fabrics: 100% cotton drill, poly-cotton 65/35, FR-treated cotton",
      "Weights: 220 – 280 GSM",
      "Sizes: 36 – 50 (S to 5XL)",
      "MOQ: 25 pieces · Lead time: 10–15 working days",
    ],
    typesTitle: "Coverall types we make",
    types: coverallTypes,
    tag: "Made-to-order",
  },
  {
    id: "cargos",
    title: "Cargo Trousers",
    icon: Scissors,
    bg: "#00F0FF",
    fg: "#0A0A0A",
    blurb:
      "Utility cargo pants for site crews, event staff, adventure teams and streetwear brands. Six-pocket construction with reinforced knees, drawstring or belt-loop waist, tapered or straight leg on request.",
    specs: [
      "Fabrics: cotton twill, ripstop cotton, poly-cotton, denim",
      "Weights: 240 – 320 GSM",
      "Sizes: 28 – 44 waist · custom inseams available",
      "Customisation: contrast pocket flaps, reflective tape, brand embroidery, custom rivets",
      "MOQ: 25 pieces · Lead time: 10–15 working days",
    ],
    tag: "Made-to-order",
  },
  {
    id: "safety-jackets",
    title: "Safety & Reflective Jackets",
    icon: Zap,
    bg: "#FF6A00",
    fg: "#0A0A0A",
    blurb:
      "Hi-Viz safety jackets for road crews, construction sites, traffic teams, warehouse pickers and event marshals. Fluorescent base fabric with EN ISO 20471-compliant 2\" silver reflective tape on chest, shoulders, sleeves and hem.",
    specs: [
      "Colours: Fluorescent Orange, Fluorescent Yellow, Lime Green, Red",
      "Styles: Full-zip jacket, half-sleeve vest, mesh vest, waistcoat, bomber",
      "Reflective tape: 2\" 3M-grade silver, sew-on or heat-transfer",
      "Sizes: S – 5XL · custom logo panels welcome",
      "MOQ: 25 pieces · Lead time: 10–14 working days",
    ],
    tag: "Hi-Viz",
  },
  {
    id: "windcheaters",
    title: "Windcheaters & Jackets",
    icon: CloudRain,
    bg: "#FF0055",
    fg: "#FFFFFF",
    blurb:
      "Lightweight water-resistant windcheaters for monsoon merchandise, college fests, marathon kits and delivery teams. Hooded or hood-less, full-zip, with elasticated cuffs and drawstring hem.",
    specs: [
      "Fabrics: micro-polyester, taffeta, NS lining, fleece-lined options",
      "Finish: water-repellent, breathable, reversible variants available",
      "Sizes: S – 5XL (kids sizes on request)",
      "Customisation: chest / back screen-print, sleeve embroidery, sublimated panels, custom zip pulls",
      "MOQ: 25 pieces · Lead time: 12–18 working days",
    ],
    tag: "Made-to-order",
  },
];

const advantages = [
  { icon: Ruler, label: "Custom sizing" },
  { icon: Palette, label: "Any colour / Pantone match" },
  { icon: Truck, label: "Pan-India delivery" },
];

export default function Workwear() {
  return (
    <section
      id="workwear"
      className="border-b-4 border-black py-20 lg:py-28 bg-[#FDFDFD]"
      data-testid="workwear-section"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
        {/* Header */}
        <div className="grid grid-cols-12 gap-8 mb-12 lg:mb-16">
          <div className="col-span-12 lg:col-span-8">
            <div className="font-heading uppercase text-xs tracking-[0.3em] text-[#555] mb-4">
              /// Workwear · Dangris · Cargos · Hi-Viz · Outerwear
            </div>
            <h2 className="font-heading uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              <span className="bg-[#FF0055] text-white nb-border-2 px-2 inline-block">
                Built for work.
              </span>
              <br />
              Made to your spec.
            </h2>
            <p className="mt-5 max-w-2xl text-base md:text-lg leading-relaxed text-[#0A0A0A]/80">
              Beyond t-shirts — Customade manufactures industrial coveralls
              (dangris / boiler suits), cargo trousers, hi-viz safety jackets
              and windcheaters for factories, delivery fleets, road-crews and
              event teams. Every piece is stitched to your fabric, colour,
              fit and branding.
            </p>
          </div>

          <div className="col-span-12 lg:col-span-4 flex flex-wrap items-end gap-2 content-end">
            {advantages.map((a) => {
              const A = a.icon;
              return (
                <span
                  key={a.label}
                  className="inline-flex items-center gap-2 nb-border-2 bg-white px-3 py-2 font-heading uppercase text-xs tracking-wider"
                  data-testid={`workwear-advantage-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <A size={14} strokeWidth={2.5} className="text-[#FF0055]" />
                  {a.label}
                </span>
              );
            })}
          </div>
        </div>

        {/* Editorial montage banner — 4-panel product family shot */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="relative nb-border-4 nb-shadow-lg overflow-hidden bg-white mb-6 md:mb-8"
          data-testid="workwear-montage"
        >
          <img
            src={HERO_MONTAGE}
            alt="Customade workwear range — denim coverall, hi-viz orange safety jacket with reflective tape, olive-green two-piece coverall, and navy plain coverall"
            className="w-full h-auto block"
            loading="lazy"
          />
          <div className="absolute top-3 left-3 md:top-5 md:left-5 nb-border-2 bg-[#FF0055] text-white px-3 py-1.5 font-heading uppercase text-xs md:text-sm tracking-wider">
            The workwear range · Denim · Hi-Viz · Cotton · Two-piece
          </div>
        </motion.div>

        {/* Editorial coverall hero — flatlay + on-the-job side by side */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-5 mb-12 lg:mb-16"
          data-testid="workwear-hero-media"
        >
          <div className="relative nb-border-4 nb-shadow-lg overflow-hidden bg-white md:col-span-2 aspect-[4/5]">
            <img
              src={HERO_FLATLAY}
              alt="Navy cotton drill boiler suit / dangri coverall — flat lay product shot"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute top-3 left-3 nb-border-2 bg-[#FFE600] px-3 py-1.5 font-heading uppercase text-xs tracking-wider">
              Product · Cotton Drill
            </div>
          </div>
          <div className="relative nb-border-4 nb-shadow-lg overflow-hidden bg-white md:col-span-3 aspect-[4/5] md:aspect-auto">
            <img
              src={HERO_ACTION}
              alt="Mechanic wearing custom Midwest Mechanics embroidered coverall while working on a lathe"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute top-3 left-3 nb-border-2 bg-black text-[#FFE600] px-3 py-1.5 font-heading uppercase text-xs tracking-wider">
              On the job · Embroidered
            </div>
            <div className="absolute bottom-3 left-3 right-3 nb-border-2 bg-white/95 backdrop-blur px-3 py-2 font-heading uppercase text-xs md:text-sm tracking-wider">
              Midwest Mechanics · Chest logo + name-tape embroidery
            </div>
          </div>
        </motion.div>

        {/* Product cards */}
        <div className="grid grid-cols-1 gap-6 lg:gap-8" data-testid="workwear-products">
          {products.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="relative nb-border-4 nb-shadow overflow-hidden"
                style={{ background: p.bg, color: p.fg }}
                data-testid={`workwear-card-${p.id}`}
              >
                <div className="grid grid-cols-12 gap-0">
                  {/* Left: content */}
                  <div className="col-span-12 lg:col-span-8 p-6 md:p-8">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div
                        className="inline-block nb-border-2 px-2 py-0.5 font-heading uppercase text-[10px] tracking-widest"
                        style={{ background: p.fg, color: p.bg }}
                      >
                        {p.tag}
                      </div>
                      <Icon size={40} strokeWidth={2} className="opacity-90" />
                    </div>

                    <h3 className="font-heading uppercase text-3xl md:text-4xl leading-tight">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-sm md:text-base leading-relaxed opacity-95 max-w-3xl">
                      {p.blurb}
                    </p>

                    <ul className="mt-5 space-y-2 max-w-3xl">
                      {p.specs.map((b) => (
                        <li
                          key={b}
                          className="text-sm md:text-[15px] leading-snug flex gap-2 items-start"
                        >
                          <span
                            className="mt-1 w-2 h-2 nb-border-2 flex-shrink-0"
                            style={{ background: p.fg, borderColor: p.fg }}
                          />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Sub-types (only for coveralls) */}
                    {p.types && (
                      <div className="mt-6">
                        <div
                          className="font-heading uppercase text-xs tracking-[0.3em] mb-3 opacity-80"
                          data-testid="coverall-types-title"
                        >
                          {p.typesTitle}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {p.types.map((t) => {
                            const T = t.icon;
                            return (
                              <div
                                key={t.name}
                                className="nb-border-2 bg-white text-[#0A0A0A] p-3 flex gap-3 items-start"
                                data-testid={`coverall-type-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                              >
                                <T size={22} strokeWidth={2.5} className="text-[#FF0055] flex-shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                  <div className="flex items-baseline gap-2 flex-wrap">
                                    <span className="font-heading uppercase text-sm leading-tight">
                                      {t.name}
                                    </span>
                                    <span className="nb-border-2 bg-[#FFE600] px-1.5 py-0 font-heading uppercase text-[9px] tracking-widest">
                                      {t.tag}
                                    </span>
                                  </div>
                                  <p className="text-[12px] leading-snug text-[#0A0A0A]/75 mt-1">
                                    {t.blurb}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <a
                      href="#contact"
                      className="mt-6 nb-btn nb-btn-black inline-flex"
                      data-testid={`workwear-card-cta-${p.id}`}
                    >
                      Send me a sample <ArrowRight size={16} strokeWidth={3} />
                    </a>
                  </div>

                  {/* Right: image band (only for coveralls with imageBanner) */}
                  {p.imageBanner && (
                    <div className="col-span-12 lg:col-span-4 relative border-t-4 lg:border-t-0 lg:border-l-4 border-black min-h-[280px]">
                      <img
                        src={HERO_FLATLAY}
                        alt="Navy cotton drill boiler suit"
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* Pricing / bulk footer */}
        <div className="mt-12 nb-border-4 bg-black text-white p-5 md:p-7 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-heading uppercase text-xs tracking-[0.3em] text-[#FFE600] mb-1">
              Rates &amp; bulk pricing
            </div>
            <div className="font-heading uppercase text-lg md:text-xl leading-tight">
              Rates vary by fabric, GSM, treatment (FR / reflective) and quantity.
              <br className="hidden md:block" />
              Share your requirement — we quote within an hour on WhatsApp.
            </div>
          </div>
          <a
            href="https://wa.me/918169272803?text=Hi%20Customade%2C%20please%20share%20rates%20for%20coveralls%2Fdangris%20and%20cargo%20trousers."
            target="_blank"
            rel="noopener noreferrer"
            className="nb-btn nb-btn-magenta"
            data-testid="workwear-quote-cta"
          >
            Ask for rates <ArrowRight size={18} strokeWidth={3} />
          </a>
        </div>
      </div>
    </section>
  );
}
